import { MoreIcon } from '@persona-id/design-tokens/icons';
import { compact, findIndex, keyBy, snakeCase, uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import { Fragment, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ClickableDetail from 'dashboard/components/ClickableDetail';
import FieldAttributeItem from 'dashboard/components/FieldAttributeItem';
import FlagIssuesModal from 'dashboard/components/FlagIssuesModal';
import InquiryDocuments from 'dashboard/components/InquiryDocuments';
import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import {
  formatAddressFieldDisplay,
  formatDate,
  formatDateTimeRangeDuration,
} from 'dashboard/lib/utils/formatters';
import { BankStatementDataField, DocumentGeneric } from 'dashboard/models/document';
import { EnvironmentStatus } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { Verification, VerificationGovernmentId } from 'dashboard/models/verification';
import { calculateHideOrShowAttribute } from 'dashboard/modules/Resource/calculateHideOrShow';
import ResourceTags from 'dashboard/modules/Resource/ResourceTags';
import { ResourceInquiryAttribute } from 'dashboard/modules/ResourceInquiry';
import InquiryPhotos from 'dashboard/modules/ResourceInquiry/InquiryPhotos';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Heading, Label } from 'design-system/components/Text';
import { FontWeight } from 'design-system/variables/text';
import { RELATION_FIELDS } from 'lib/fields';
import useCurrentEnvironmentStatus from 'lib/hooks/useCurrentEnvironmentStatus';
import useCurrentEnvironmentType from 'lib/hooks/useCurrentEnvironmentType';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { Permission } from 'lib/permissions';
import { nameDisplay } from 'lib/utils/template';
import { VerificationStatus } from 'lib/verification';

const StyledMenu = styled(DropdownMenu)`
  min-width: 200px;
`;

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InquiryNameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TimeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 20px;
`;

const LinkRow = styled('div')`
  grid-column-end: span 3;
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
  font-weight: ${FontWeight.Regular};
`;

export interface ResourceInquiryProps {
  attributes: ResourceInquiryAttribute[];
  hideEmptyAttributes?: boolean;
  inquiry: Inquiry;
  documents: DocumentGeneric[];
  inquiryTemplates: (InquiryTemplate | Template)[];
  inquiryTemplateVersions: InquiryShowViewInquiryTemplateVersion[];
  verifications: Verification[];
  tags: Tag[];
  defaultAccordionOpen: boolean;
  featureFlags: FeatureFlagsMap;
  handleUpdateInquiryTags: (inquiryId: string, tags: string[]) => void;
  handleFlagInquiryIssues: (
    inquiry: Inquiry,
    verifications: Verification[],
    values: FlagIssuesFormData,
  ) => Promise<void>;
}

const ResourceInquiry = ({
  attributes,
  hideEmptyAttributes = false,
  inquiry,
  documents,
  inquiryTemplates,
  inquiryTemplateVersions,
  verifications,
  tags,
  defaultAccordionOpen,
  featureFlags,
  handleUpdateInquiryTags,
  handleFlagInquiryIssues,
}: ResourceInquiryProps) => {
  const {
    birthdate,
    'social-security-number': socialSecurityNumber,
    'address-city': addressCity,
    'address-postal-code': addressPostalCode,
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-subdivision': addressSubdivision,
    'address-country-code': addressCountryCode,
    'name-full': nameFull,
    'name-full-localized': nameFullLocalized,
    'phone-number': phoneNumber,
    'email-address': emailAddress,
    'reviewer-comment': reviewerComment,
    'created-at': createdAt,
    'completed-at': completedAt,
  } = inquiry.attributes;

  const [
    reportInquiryIssues,
    { enable: showReportInquiryIssues, disable: hideReportInquiryIssues },
  ] = useToggle(false);

  const { completionTime, dateTimeRange } = formatDateTimeRangeDuration(createdAt, completedAt);

  const { type: environmentType } = useCurrentEnvironmentType();
  const environmentStatus = useCurrentEnvironmentStatus();

  const handleUpdateTags = (tags: string[]) => handleUpdateInquiryTags(inquiry.id, tags);

  const handleFlagInquiryIssuesAndClose = useCallback(
    async (values: FlagIssuesFormData) => {
      await handleFlagInquiryIssues(inquiry, verifications, values);
      hideReportInquiryIssues();
    },
    [handleFlagInquiryIssues, hideReportInquiryIssues, inquiry, verifications],
  );

  const template: Template | InquiryTemplate = inquiryTemplates.find((obj) =>
    [
      inquiry.relationships['inquiry-template'].data?.id,
      inquiry.relationships['template'].data?.id,
    ].includes(obj.id),
  ) as Template | InquiryTemplate;

  const inquiryTemplateVersion = inquiryTemplateVersions.find(
    (obj) => inquiry.relationships['inquiry-template-version'].data?.id === obj.id,
  );

  // Snake case because we inflect field keys for `fields` but not for `field-schemas`.
  // Ideally we don't inflect field keys, ever.
  const fieldSchemaMap = useMemo(
    () =>
      keyBy(
        ({ key }) => snakeCase(key),
        (template?.type === ApiModel.Template
          ? template.attributes['v2-field-schemas']
          : inquiryTemplateVersion?.attributes['field-schema']) ?? [],
      ),
    [template, inquiryTemplateVersion],
  );

  const isRedacted = inquiry.attributes['redacted-at'] != null;

  const governmentIdVerifications: VerificationGovernmentId[] = verifications.filter(
    (v): v is VerificationGovernmentId => {
      return v.type === ApiModel.VerificationGovernmentId;
    },
  );

  const passedGovIdVerifications = governmentIdVerifications.filter(
    (v) => v.attributes.status === VerificationStatus.Passed,
  );

  let governmentIdentificationNumbers, govIdExpirationDate;

  if (passedGovIdVerifications.length > 0) {
    govIdExpirationDate = passedGovIdVerifications[0].attributes['expiration-date'];

    governmentIdentificationNumbers = compact(
      uniq(
        passedGovIdVerifications.map(
          (v: VerificationGovernmentId) => v.attributes['identification-number'],
        ),
      ),
    );
  } else {
    govIdExpirationDate =
      governmentIdVerifications.length > 0
        ? governmentIdVerifications[0].attributes['expiration-date']
        : '';

    governmentIdentificationNumbers = compact(
      uniq(
        governmentIdVerifications.map(
          (v: VerificationGovernmentId) => v.attributes['identification-number'],
        ),
      ),
    );
  }

  const formattedBirthdate =
    birthdate != null ? (
      <div>
        <p>{formatDate(birthdate)}</p>
        <p>
          ({Math.floor(DateTime.local().diff(DateTime.fromISO(birthdate), 'years').years)} years
          old)
        </p>
      </div>
    ) : undefined;

  const address = formatAddressFieldDisplay({
    addressStreet1: addressStreet1,
    addressStreet2: addressStreet2,
    addressCity: addressCity,
    addressSubdivision: addressSubdivision,
    addressPostalCode: addressPostalCode,
    addressCountryCode: addressCountryCode,
  });

  const truncatedSSN = socialSecurityNumber?.substring(socialSecurityNumber?.length - 4);

  const fields = inquiry.attributes.fields ?? {};
  const fieldNamesToShow = Object.keys(fields)
    .filter((fieldName) => fieldSchemaMap[snakeCase(fieldName)] != null)
    .filter((fieldName) => fields[fieldName] != null)
    .filter((fieldName) => !RELATION_FIELDS.has(fields[fieldName].type))
    .filter((fieldName: string) => {
      // NOTE: We don't key inflect the network response here
      return !Object.values(PiiAttributes).map(snakeCase).includes(snakeCase(fieldName));
    });

  const nameAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    nameFullLocalized ?? nameFull,
    <Attribute
      label='Name'
      value={nameFullLocalized ?? nameFull}
      redacted={isRedacted}
      placeholder='No name collected'
      key={ResourceInquiryAttribute.Name}
    />,
  );

  const governmentIdNumberAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    governmentIdentificationNumbers[0],
    <Attribute
      label='Government ID Number'
      value={
        <Permissioned
          permission={Permission.CaseGovIdNumberView}
          fallback={<Sticker variant='basic'>Not Available</Sticker>}
        >
          {governmentIdentificationNumbers}
        </Permissioned>
      }
      redacted={isRedacted}
      placeholder='No government ID number collected'
      key={ResourceInquiryAttribute.GovernmentIDNumber}
    />,
  );

  const birthdateAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    formattedBirthdate,
    <Attribute
      label='Birthdate'
      value={formattedBirthdate}
      redacted={isRedacted}
      placeholder='No birthdate collected'
      key={ResourceInquiryAttribute.BirthDate}
    />,
  );

  const addressAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    address,
    <Attribute
      label='Address'
      value={address}
      redacted={isRedacted}
      placeholder='No address collected'
      key={ResourceInquiryAttribute.Address}
    />,
  );

  const expirationDateAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    govIdExpirationDate,
    <Attribute
      label='Expiration date'
      value={govIdExpirationDate && formatDate(govIdExpirationDate)}
      redacted={isRedacted}
      placeholder='No expiration date collected'
      key={ResourceInquiryAttribute.ExpirationDate}
    />,
  );

  const emailAddressAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    emailAddress,
    <Attribute
      label='Email address'
      value={emailAddress}
      redacted={isRedacted}
      placeholder='No email address collected'
      key={ResourceInquiryAttribute.EmailAddress}
    />,
  );

  const phoneNumberAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    phoneNumber,
    <Attribute
      label='Phone Number'
      value={phoneNumber}
      redacted={isRedacted}
      placeholder='No phone number collected'
      key={ResourceInquiryAttribute.PhoneNumber}
    />,
  );

  const ssnAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    truncatedSSN || socialSecurityNumber,
    <Permissioned
      key={ResourceInquiryAttribute.SocialSecurityNumber}
      permission={Permission.InquirySsnView}
      fallback={
        <Attribute
          label='Social Security Number'
          value={truncatedSSN}
          redacted={isRedacted}
          toggleable={true}
          placeholder='No social security number collected'
        />
      }
    >
      <Attribute
        label='Social Security Number'
        value={socialSecurityNumber}
        redacted={isRedacted}
        toggleable={true}
        placeholder='No social security number collected'
      />
    </Permissioned>,
  );

  const reviewerCommentAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    reviewerComment,
    <Attribute
      label='Reviewer comment'
      value={reviewerComment}
      redacted={isRedacted}
      placeholder='No comment collected'
      key={ResourceInquiryAttribute.ReviewerComment}
    />,
  );

  const moreItems = useMemo(() => {
    return [
      {
        id: 'resource-inquiry-header__report_issues',
        name: 'resource-inquiry-header__report_issues',
        onClick: showReportInquiryIssues,
        label: 'Report issue',
        variant: 'basic' as const,
        hidden: environmentType === ApiModel.EnvironmentSandbox,
        disabled: environmentStatus !== EnvironmentStatus.Active,
      },
    ].filter((item) => !item.hidden);
  }, [environmentType, environmentStatus, showReportInquiryIssues]);

  const fieldsAttribute = fieldNamesToShow.map((fieldName: string) => {
    // FIXME: Inquiries should not expect hardcoded field names.
    // There's work in progress to parameterize these field names
    // instead of assuming certain field names exist.
    if (fieldName === BankStatementDataField) return null;

    const fieldValue = inquiry.attributes.fields[fieldName];
    const fieldSchema = fieldSchemaMap[snakeCase(fieldName)];

    return (
      <FieldAttributeItem
        key={fieldSchema.key}
        field={fieldValue}
        fieldSchema={fieldSchema}
        isRedacted={isRedacted}
        hideEmptyAttributes={hideEmptyAttributes}
        wordBreak='break-all'
      />
    );
  });

  const documentExtractionAttribute = calculateHideOrShowAttribute(
    hideEmptyAttributes,
    documents && documents.length > 0,
    <Fragment key={`resource-inquiry__${ResourceInquiryAttribute.DocumentExtraction}`}>
      <Spacer height={4} />
      {documents && documents.length > 0 ? (
        <InquiryDocuments
          isRedacted={isRedacted}
          template={template}
          documents={documents}
          featureFlags={featureFlags}
        />
      ) : (
        <Attribute label='Document extractions' placeholder='No documents extracted' />
      )}
      <Spacer height={4} />
    </Fragment>,
  );

  const getAttributeByType = (attr: ResourceInquiryAttribute) => {
    switch (attr) {
      case ResourceInquiryAttribute.Name:
        return nameAttribute;
      case ResourceInquiryAttribute.GovernmentIDNumber:
        return governmentIdNumberAttribute;
      case ResourceInquiryAttribute.BirthDate:
        return birthdateAttribute;
      case ResourceInquiryAttribute.Address:
        return addressAttribute;
      case ResourceInquiryAttribute.ExpirationDate:
        return expirationDateAttribute;
      case ResourceInquiryAttribute.EmailAddress:
        return emailAddressAttribute;
      case ResourceInquiryAttribute.PhoneNumber:
        return phoneNumberAttribute;
      case ResourceInquiryAttribute.SocialSecurityNumber:
        return ssnAttribute;
      case ResourceInquiryAttribute.Fields:
        return fieldsAttribute;
      case ResourceInquiryAttribute.ReviewerComment:
        return reviewerCommentAttribute;
    }
  };

  const showPhotos =
    findIndex((attr) => attr === ResourceInquiryAttribute.Photos, attributes) !== -1;

  const showDocuments =
    findIndex((attr) => attr === ResourceInquiryAttribute.DocumentExtraction, attributes) !== -1;

  const showTags = findIndex((attr) => attr === ResourceInquiryAttribute.Tags, attributes) !== -1;

  return (
    <>
      <ClickableDetail
        header={
          <HeaderContainer>
            <Header>
              <InquiryNameContainer>
                <Heading size='sm'>Inquiry - {nameDisplay(template)}</Heading>
                <Spacer width={1} />
                <InquiryStatusSticker status={inquiry.attributes.status} />
              </InquiryNameContainer>
              <TimeRangeContainer>
                <Label size='md' color='tertiary' weight='bold'>
                  {dateTimeRange}
                </Label>
                {completionTime != null && (
                  <>
                    <Spacer height={0} width={1} />
                    <Label size='md' color='tertiary' weight='bold'>
                      {completionTime}
                    </Label>
                  </>
                )}
              </TimeRangeContainer>
            </Header>
          </HeaderContainer>
        }
        subheader={
          <LinkRow>
            <StyledLink to={`/dashboard/inquiries/${inquiry.id}`}>{inquiry.id}</StyledLink>
          </LinkRow>
        }
        dropdownMenu={
          <StyledMenu
            trigger={
              <Button
                id='resource-inquiry-menu__open-menu-button'
                width='icon'
                variant='inverse'
                aria-label='More actions'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <MoreIcon />
              </Button>
            }
          >
            {moreItems.map((item) => (
              <DropdownMenu.Item
                id={item.id}
                variant={item.variant}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  item.onClick();
                }}
                disabled={item.disabled}
                key={item.id}
              >
                {item.label}
              </DropdownMenu.Item>
            ))}
          </StyledMenu>
        }
        isDefaultOpen={defaultAccordionOpen}
      >
        <Card>
          <Card.Body>
            <AttributeGrid colCount={3}>
              {attributes.map((attribute) => getAttributeByType(attribute))}
            </AttributeGrid>
            {showPhotos && (
              <InquiryPhotos
                inquiry={inquiry}
                verifications={verifications}
                isRedacted={isRedacted}
              />
            )}
            {showDocuments && documentExtractionAttribute}
            {showTags && (
              <ResourceTags
                tags={tags}
                tagScope={ApiModel.Inquiry}
                editTagPermission={Permission.InquiryWrite}
                onUpdateTags={handleUpdateTags}
              />
            )}
          </Card.Body>
        </Card>
      </ClickableDetail>

      <ModalWrapper isOpen={reportInquiryIssues} handleCloseModal={hideReportInquiryIssues}>
        <FlagIssuesModal
          verifications={verifications}
          documents={documents}
          handleClose={hideReportInquiryIssues}
          handleSubmitFlagIssues={handleFlagInquiryIssuesAndClose}
        />
      </ModalWrapper>
    </>
  );
};

export default ResourceInquiry;
